import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "../styles/AddCardsModal.scss";
import ChartPreviewIcon from "./charts/add-modal/ChartPreviewIcon";

// Types
interface SelectedTemplate {
  id: string;
  title: string;
  module: string;
  configKey: string;
  chartType?: string;
  componentType: "metric" | "chart";
  icon: string;
  selected: boolean;
  isUsed: boolean;
  isDisabled: boolean;
  description: any;
  valueFormat?: any;
}

interface AddCardsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCards: (selectedCards: any[]) => void;
  availableTemplates: {
    metrics: Record<string, any[]>;
    charts: Record<string, any[]>;
  };
  usedComponents: string[];
  modalType: "metrics" | "charts";
}

const AddCardsModal: React.FC<AddCardsModalProps> = ({
  isOpen,
  onClose,
  onAddCards,
  availableTemplates,
  usedComponents,
  modalType,
}) => {
  // State
  const [selectedTemplates, setSelectedTemplates] = useState<
    SelectedTemplate[]
  >([]);
  const [sortBy, setSortBy] = useState<"module" | "type">("module");
  const [searchTerm, setSearchTerm] = useState("");
  const [templates, setTemplates] = useState<SelectedTemplate[]>([]);

  // Helper Functions
  const convertTemplatesToSelectableItems = (): SelectedTemplate[] => {
    const templateSource =
      modalType === "metrics"
        ? availableTemplates.metrics
        : availableTemplates.charts;

    const items: SelectedTemplate[] = [];

    Object.entries(templateSource).forEach(([module, moduleTemplates]) => {
      moduleTemplates.forEach((template) => {
        const componentKey = `${module}.${template.configKey}`;
        const isUsed = usedComponents.includes(componentKey);

        items.push({
          id: `${module}-${template.configKey}`,
          title: template.title,
          module,
          configKey: template.configKey,
          componentType: template.type,
          icon: template.icon || "ti-square",
          description: template.description,
          selected: false,
          isUsed,
          isDisabled: isUsed,
          chartType: template?.chartType,
          valueFormat: template?.valueFormat,
        });
      });
    });

    return items;
  };

  // Computed Values
  const filteredTemplates = templates.filter(
    (template) =>
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.module.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    const key = sortBy === "module" ? template.module : template.componentType;
    if (!acc[key]) acc[key] = [];
    acc[key].push(template);
    return acc;
  }, {} as Record<string, SelectedTemplate[]>);

  const usedTemplatesCount = templates.filter((t) => t.isUsed).length;

  // Get modal title and labels based on type
  const getModalConfig = () => {
    if (modalType === "metrics") {
      return {
        title: "Add Metrics",
        searchPlaceholder: "Search metrics...",
        emptyMessage: "No available metrics found matching your search",
        allUsedMessage: "All metrics have been added to your dashboard!",
        icon: "ti-dashboard",
      };
    } else {
      return {
        title: "Add Charts",
        searchPlaceholder: "Search charts...",
        emptyMessage: "No available charts found matching your search",
        allUsedMessage: "All charts have been added to your dashboard!",
        icon: "ti-chart-bar",
      };
    }
  };

  const modalConfig = getModalConfig();

  // Event Handlers
  const toggleTemplateSelection = useCallback(
    (templateId: string) => {
      const template = templates.find((t) => t.id === templateId);
      if (template?.isDisabled) return;

      const updatedTemplates = templates.map((template) =>
        template.id === templateId
          ? { ...template, selected: !template.selected }
          : template
      );
      setTemplates(updatedTemplates);
      setSelectedTemplates(
        updatedTemplates.filter((template) => template.selected)
      );
    },
    [templates]
  );

  const handleAddSelectedTemplates = useCallback(() => {
    if (selectedTemplates.length === 0) return;

    const componentsToAdd = selectedTemplates.map((template) => ({
      id: template.id,
      title: template.title,
      module: template.module,
      configKey: template.configKey,
      componentType: template.componentType,
    }));

    onAddCards(componentsToAdd);
    onClose();
  }, [selectedTemplates, onAddCards, onClose]);

  // Effects
  useEffect(() => {
    if (isOpen) {
      setTemplates(convertTemplatesToSelectableItems());
      setSelectedTemplates([]);
      setSearchTerm("");
    }
  }, [isOpen, modalType, availableTemplates, usedComponents]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="add-cards-modal-overlay">
      <div className="add-cards-modal">
        {/* Header */}
        <div className="add-cards-modal-header">
          <div className="header-left">
            <i className={`ti ${modalConfig.icon} header-icon`} />
            <h2 className="add-cards-modal-title">{modalConfig.title}</h2>
          </div>
          <span className="add-cards-selection-count">
            {selectedTemplates.length} selected, {usedTemplatesCount} in use
          </span>
          <button className="add-cards-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Controls */}
        <div className="add-cards-modal-controls">
          <div className="add-cards-search-container">
            <i className="ti ti-search" />
            <input
              type="text"
              placeholder={modalConfig.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="add-cards-search-input"
            />
          </div>

          <button
            className="add-cards-button"
            disabled={selectedTemplates.length === 0}
            onClick={handleAddSelectedTemplates}>
            Add {selectedTemplates.length}{" "}
            {selectedTemplates.length === 1
              ? modalType === "metrics"
                ? "Metric"
                : "Chart"
              : modalType === "metrics"
              ? "Metrics"
              : "Charts"}
          </button>
        </div>

        {/* Content */}
        <div className="add-cards-modal-content">
          {Object.entries(groupedTemplates).length === 0 ? (
            <div className="add-cards-empty-state">
              <i className="ti ti-search text-muted" />
              <p className="text-muted">{modalConfig.emptyMessage}</p>
            </div>
          ) : (
            Object.entries(groupedTemplates).map(
              ([groupKey, groupTemplates]) => (
                <div key={groupKey} className="add-cards-group">
                  <div className="add-cards-group-header">
                    <span className="add-cards-group-title">
                      {groupKey.charAt(0).toUpperCase() + groupKey.slice(1)}
                    </span>
                  </div>

                  <div className="add-cards-grid">
                    {groupTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={`add-cards-item ${
                          template.selected ? "selected" : ""
                        } ${template.isDisabled ? "disabled" : ""}`}
                        onClick={() =>
                          !template.isDisabled &&
                          toggleTemplateSelection(template.id)
                        }>
                        <div className="add-cards-checkbox">
                          <input
                            type="checkbox"
                            checked={template.selected}
                            disabled={template.isDisabled}
                            onChange={() => {}}
                            className="add-cards-checkbox-input"
                          />
                        </div>

                        <div className="add-cards-content">
                          <div className="add-cards-header">
                            <i
                              className={`ti ${template.icon} add-cards-icon`}
                            />
                            <div className="add-cards-title">
                              {template.title}
                            </div>
                          </div>

                          <div className="add-cards-subtitle">
                            {template.description}
                          </div>

                          <div className="add-cards-chart-container">
                            <ChartPreviewIcon
                              componentType={template.componentType}
                              configKey={template.configKey}
                              chartType={template.chartType}
                              valueFormat={template.valueFormat}
                            />
                          </div>

                          {template.isDisabled && (
                            <div className="disabled-overlay"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddCardsModal;
